import { FC } from 'react';
import { useTranslation } from '@packages/utils';
import EditableSelect from 'components/Base/EditableFields/EditableSelect';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useEditTypeMutation } from 'redux/apiSlice/events.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import { EEventCategory } from 'data/types/events.types';
import { Flex } from 'antd';
import { InfoOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import useGroupedEventTypeData from 'pages/private/Events/components/GroupedEventTypeSelect/GroupedEventTypeSelect.data';
import {
  GroupedDropdownStyles,
  DROPDOWN_CLASS,
} from 'pages/private/Events/components/GroupedEventTypeSelect/GroupedEventTypeSelect.styles';
import { TTypeProps } from './Type.types';

const Type: FC<TTypeProps> = ({ id, type, typeCategory, readonly }) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.basic-info.type',
  });
  const { t: tGlobal } = useTranslation();
  const { showToastNotification } = useShowToastNotification();
  const { groups } = useGroupedEventTypeData();
  const [editType, { isLoading, isError }] = useEditTypeMutation();

  // Cross-category type changes are rejected by the backend, and the two
  // series categories each contain a single type — there is no other legal
  // target, so the row stays read-only. All dropdown options are STANDALONE.
  const isSingleTypeCategory =
    !!typeCategory && typeCategory !== EEventCategory.STANDALONE;

  const onEdit = async (value: string) => {
    try {
      await editType({
        id,
        value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (e) {
      showToastNotification({
        message: (e as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-type" gap={8}>
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <InfoOutline size={24} />
        <Text color="#808080">{t('LABEL')}</Text>
      </Flex>
      <GroupedDropdownStyles />
      <EditableSelect
        onEdit={onEdit}
        title={{
          label: type ? tGlobal(`enums.event-type.${type}`) : '',
          value: type || '',
        }}
        options={groups}
        emptyText={t('EMPTY_TEXT')}
        readonly={readonly || isSingleTypeCategory}
        isLoading={isLoading}
        isError={isError}
        canRemove={false}
        selectWidth={500}
        selectProps={{ popupClassName: DROPDOWN_CLASS }}
      />
    </Flex>
  );
};

export default Type;
